import { getFirstAvSubUnitFromArr, shouldShowPitchMap } from "../utils/filters.js";
import {
    filterElementsByParentId,
    showOptionsWithParentId,
    toggle,
    announce
} from "../utils/ui-helpers.js"
import { qsAll, elById, qs } from "../utils/dom.js";
import { 
    resetDropdown, 
    setDropdown, 
    setDropdownMessage,
    showDropdownOptsInArray, 
    showDropdownOptsByParentId, 
    toggleDropdownValidation,
    toggleDropdownOptions,
    updateSubUnitPitchSizeLabels
} from "./dropdown.js";
import { showModal } from "./third-party/modal.js";

import * as summary from "./checkout-summary.js";

import { initialisePartyMemberSteppers, buildPartyMemberDetailsContainers } from "./party-members.js";
import { resetExtrasUI } from "./extras.js";

import { updatePaymentPlanSelectors, updatePaymentSelectedClass } from "../payment-selector.js";
import { calendar } from "../checkout-visual-calendar.js";
import { AvailabilityType } from "../enums/availability-type.js";
import { getBookingStateProp, getClientData, getSiteNameForUnit } from "../state.js";
import { runAction, runActionPhase } from "../actions/action-functions.js";
import { actions } from "../actions/action-config.js";


function updateOnSiteChange(bookingData) {
    updatePitchMapButton();

    // ResetDropdowns
    qsAll('#atb-category, #atb-unit, #atb-sub-unit, #atb-sub-unit-pitch-type, #atb-party-size')
        .forEach(el => resetDropdown(el));

    qsAll('#atb-sub-unit-row, #atb-sub-unit-pitch-type-row, #atb-party-size-row, #atb-filter-pet-friendly-row, #atb-party-members-section')
        .forEach(el => toggle(el, false));

    elById('atb-period').value = 0;

    // Destroy calendar - incase it's been built before
    calendar.destroy({doHideWrapper: true});
    // show all categories belonging to the chosen site
    showDropdownOptsByParentId('category', bookingData.site);       

    summary.resetSummaryHead();
    summary.updateSummary(bookingData.costs);
}

function updateOnCategoryChange(bookingData) {
    updatePitchMapButton();

    // reset UI if we've moved backwards in the booking process
    qsAll('#atb-unit, #atb-sub-unit, #atb-sub-unit-pitch-type, #atb-party-size')
        .forEach(el => resetDropdown(el));
    
    // Hide all UI dependent on 'unit' (unit related dropdowns)
    qsAll('#atb-sub-unit-row, #atb-sub-unit-pitch-type-row, #atb-party-size-row, #atb-filter-pet-friendly-row, #atb-party-members-section')
        .forEach(el => toggle(el, false));

    resetPetFriendlyFilterCheckbox();

    elById('atb-period').value = 0;

    calendar.destroy({doHideWrapper: true});
    showDropdownOptsByParentId('unit', bookingData.category);
    
    summary.resetSummaryHead();
    summary.updateSummary(bookingData.costs);
    updateReferralDropdown();
}

function updateOnOccupancyPartySizeChange(currentBookingData) {
     // reveal the calendar
    calendar.destroy({doHideWrapper: true});
    calendar.generate(
        '', // no start date needed for occupancy-based availability calendar
        1, // default length of stay
        { 
            usingPreset: false, 
            findFirstAvailable: true, 
            partySize: currentBookingData.party_size,
            availabilityType: AvailabilityType.Occupancy
        }
    );
    calendar.resetBtnText();

    toggleInputDrawer('calendar', true);
    toggle(elById('atb-calendar-row'), true);
    announce('Availability calendar is now shown below');
}

function updateOnUnitChange(currentBookingData, unit, referrals = []) {
    // reset UI if we've moved backwards in the booking process
    qsAll('#atb-sub-unit, #atb-sub-unit-pitch-type, #atb-party-size')
        .forEach(el => resetDropdown(el));

    updateSubUnitPitchSizeLabels(unit.show_pitch_size, getClientData().sub_units);

    // toggle validation on the sub-unit dropdown

    const subUnitDropdown = elById('atb-select-dropdown-sub-unit');
    if (subUnitDropdown) {
        toggleDropdownValidation(subUnitDropdown, currentBookingData.is_single_unit == 0);
    }

    // Hide all UI dependent on 'unit'
    qsAll('#atb-party-members-section, #atb-filter-pet-friendly-row, #atb-sub-unit-row, #atb-sub-unit-pitch-type-row')
        .forEach(el => toggle(el, false));
    updatePitchMapButton(unit);

    resetPetFriendlyFilterCheckbox();

    elById('atb-period').value = 0;

    summary.resetSummaryDatesUI();
    summary.updateSummarySubUnitUI('', false);
    togglePartySizeDropdown(false);
    
    // Pull unit out of ui data array using unit id
    const unitId = currentBookingData.unit;
    
    summary.updateSummaryUnitUI({ ...unit, siteName: getSiteNameForUnit(unit) });
    summary.updateSummary(currentBookingData.costs);
    updateReferralDropdown(referrals);

    if (parseIntb10(unit.availability_type) === AvailabilityType.Occupancy) {
        // reveal the party size 2 row
        toggle(elById('atb-party-size2-row'), true, 'flex');
        toggleDropdownOptions('party-size2', option => parseIntb10(option.dataset.value) <= currentBookingData.occupancy);

        calendar.destroy({ doHideWrapper: true });
        // calendar logic below is then only shown as per below
        // once party size 2 has been chosen
    } else {
        // all other availability types
        showDropdownOptsByParentId('sub-unit', unitId);

        toggle(elById('atb-party-size2-row'), false);

        // reveal the calendar
        toggle(elById('atb-calendar-row'), true);

        // Non-occupancy calendars use booking-derived availability, while
        // "reduce by booking" availability checks for at least one space.
        const partySize = parseIntb10(unit.availability_type) == AvailabilityType.Booking ? 1 : 0;

        calendar.generate('', 1, { partySize, findFirstAvailable: true });
        calendar.resetBtnText();

        toggleInputDrawer('calendar', true);
        announce('Availability calendar is now shown below');
    }

    // TODO: Almost certainly do not want to reset all addons whenever a dropdown changes like this
    // May be a good idea to store some state to track whether or not the addons need to be removed
    // reset addons
    resetExtrasUI();
}

function updateReferralDropdown(referrals = []) {
    const referralInput = elById('atb-referral');
    const referralOptions = elById('atb-referral-dropdown-values');

    if (!referralInput || !referralOptions) return;

    resetDropdown(referralInput);
    referralOptions.replaceChildren();

    referrals.forEach(({ id, name }) => {
        const option = document.createElement('li');
        const label = String(name || '');

        option.className = 'atb-select-option py-2 px-3';
        option.dataset.value = id;
        option.tabIndex = 0;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.setAttribute('aria-label', label);
        option.textContent = label;
        referralOptions.appendChild(option);
    });
}

function updateOnPeriodChange(bookingData) {
    const isUsingDeposit = !bookingData.costs.fullOnly;
                        //&& parseFloat(bookingData.costs.depositUnformatted) > 0;
    const period = bookingData.period;

    // TODO: hopefully move away from validating hidden inputs, we won't have to set values like this in the future
    elById('atb-period').value = period;

    // TODO: After the calendar logic has been refactored, revisit this            
    summary.updateSummaryPeriodUI(bookingData.period, true);
    summary.updateSummaryDepartureDate(bookingData.departure_date, true);
    summary.updateSummaryDatesText(bookingData.arrival_date, bookingData.departure_date);
    calendar.updateBtnPeriodText(bookingData.period);
    calendar.updateBtnDepartureText(bookingData.departure_date);
    
    // Update payment info in summary and wherever there is a 'payment selector' template being used
    updatePaymentSelectedClass(isUsingDeposit ? "deposit" : "full");
    updatePaymentPlanSelectors(isUsingDeposit, bookingData.costs);

    summary.updateSummary(bookingData.costs);
    
    const isSingleUnit = bookingData.is_single_unit;
    toggle(elById('atb-filter-pet-friendly-row'), bookingData.using_pets_filter, 'flex');
    resetPetFriendlyFilterCheckbox();
    
    // If we're dealing with a parent unit, handle the sub unit UI accordingly and return early
    if (!isSingleUnit) {        
        handleSubunitUI(bookingData);
        return;
    }

    // Otherwise, run party member logic for single units
    handlePartyMemberUI(bookingData);
}

function handlePartyMemberUI(bookingData) {
    // TODO: Party member UI handling will have to be done conditionally here - if the customer is using a sub-unit, 
    // we'll want to reveal the UI when the sub-unit has been chosen
    const {
        unit: unitId,
        using_party_size: usingPartySizeDropdown,
        party_members: partyMembers,
        availability_type: availabilityType
    } = bookingData;

    const codedUnitId = 'u' + unitId; // 'u' is the parent ID identifier for 'unit'

    // TODO: Don't like this - first, we should not have 2 party-size dropdowns and second, we should handle them in a consistent manner if we do
    toggle(elById('atb-party-size2-row'), availabilityType == AvailabilityType.Occupancy, 'flex');
    togglePartySizeDropdown(availabilityType !== AvailabilityType.Occupancy && usingPartySizeDropdown);

    // If the unit is a single unit, show whichever party members are available against that unit id
    showOptionsWithParentId('#atb-party-members-section', '.atb-stepper', codedUnitId);


    // TODO: Can move "show options with parent id" out of period now that we store current unit in state, possibly to "beforeStage" event
    showOptionsWithParentId('#atb-extras-section', '.atb-addon', codedUnitId);

    // Build a container in the party member details form for each party member (needs to run after the options are visible to work)
    buildPartyMemberDetailsContainers();

    const partyMemberSteppers = filterElementsByParentId(qsAll('.atb-party.atb-stepper'), unitId);    
    if (partyMemberSteppers.length > 0) {
        // If we're coming from a preset, we need to prepopulate party member steppers with any data passed through the URL
        initialisePartyMemberSteppers(partyMemberSteppers, partyMembers, true);
    } else {
        setDropdown(elById('atb-party-size'), '1', true);
    }
}

function updateOnSubUnitChange(bookingData, subUnits) {
    // update sub-unit name
    // update summary
    const {
        sub_unit: subUnitId,
        hide_sub_units: hideSubUnits,
    } = bookingData;
    
    const subUnit = subUnits.find(subUnit => subUnit.id == subUnitId);
    summary.updateSummarySubUnitUI(subUnit.name, !hideSubUnits);

    // qs('#atb-party-members-section .invalid-feedback').classList.remove('d-block');
    handlePartyMemberUI(bookingData);
    summary.updateSummary(bookingData.costs);
}

/**
 * Handles the various aspects of the sub-unit related fields in stage 1
 * @param {Object} bookingData current booking's state, containing all sub-unit related fields
 */
function handleSubunitUI(bookingData) {
    const {
        av_sub_units            : availableSubUnits,
        av_pitch_types          : availablePitchTypes,
        restrict_sub_units      : restrictSubUnits,
        hide_sub_units          : hideSubUnits,
        period                  : lengthOfStay,
        using_pitch_filter      : usePitchTypeDropdown,
        restrict_sub_unit_period: restrictSubUnitPeriod,
        pets_filter_active      : petsFilterActive
    } = bookingData;

    const doHideSubUnits = hasHiddenSubUnit(lengthOfStay, restrictSubUnitPeriod, Boolean(restrictSubUnits), Boolean(hideSubUnits));
    const visibleSubUnits = petsFilterActive
        ? availableSubUnits.filter(subUnit => parseIntb10(subUnit.pets_flag) == 1)
        : availableSubUnits;
    const noUnitsMessage = petsFilterActive
        ? 'NO PET FRIENDLY UNITS AVAILABLE'
        : 'NO UNITS AVAILABLE';

    // filter the sub unit options to only show those that belong to the selected unit
    showDropdownOptsInArray(
        'sub-unit',
        visibleSubUnits
    );

    if (usePitchTypeDropdown) {
        showDropdownOptsInArray(
            'sub-unit-pitch-type',
            availablePitchTypes
        );
        resetDropdown(elById('atb-sub-unit-pitch-type'), false);
    }

    // Set the sub-units dropdown to the first available sub-unit
    const firstAvSubunit = getFirstAvSubUnitFromArr(visibleSubUnits);
    const hasVisibleSubUnits = visibleSubUnits.length > 0;
    // Show sub-units and pitch-type dropdown if we're not hiding sub-units
    toggle(elById('atb-sub-unit-row'), !doHideSubUnits || !hasVisibleSubUnits, 'flex');
    toggle(elById('atb-sub-unit-pitch-type-row'), usePitchTypeDropdown && hasVisibleSubUnits, 'flex');

    const unit = getClientData().units.find(unit => unit.id == bookingData.unit);
    updatePitchMapButton(unit, visibleSubUnits);

    if (doHideSubUnits && firstAvSubunit) {        
        setDropdown(elById('atb-sub-unit'), String(firstAvSubunit.id));
        summary.updateSummarySubUnitUI(firstAvSubunit.name, false);
        handlePartyMemberUI(bookingData);
    } else if (!hasVisibleSubUnits) {
        setDropdownMessage(elById('atb-sub-unit'), noUnitsMessage);
        summary.updateSummarySubUnitUI('', true);
        resetExtrasUI();
    } else {
        resetDropdown(elById('atb-sub-unit'), false);
        summary.updateSummarySubUnitUI('', true);
    }
}

function initPresetUI(
    bookingData,
    clientData,
    unit
) {
    console.log('Preset mode detected, building party member details containers');
    // set the unit thumbnail asynchronously
    summary.setUnitThumbnailAsync({imageSrc: unit.thumbnail, imageAlt: `Image of ${unit.name}`});

    const {
        arrival_date_ymd: startDate,
        period,
        sub_unit                : subUnitId,
        is_booking_available    : isBookingAvailable,
        party_members           : partyMembers,
        party_size              : partySize,
        category                : categoryId,
        using_pitch_filter      : usingPitchFilter,
        using_party_size        : usingPartySize,
        using_pets_filter       : usingPetsFilter,
        pets_filter_active      : petsFilterActive,
        hide_sub_units          : hideSubUnits,
        restrict_sub_unit_period: restrictPeriod,
        restrict_sub_units      : restrictSubUnits,
        availability_type       : availabilityType,
        missing_arrival_date    : missingArrivalDate
    } = bookingData;

    const unitMaxOccupancy = parseIntb10(unit.occupancy);
    const unitId           = unit.id;

    updateSubUnitPitchSizeLabels(unit.show_pitch_size, clientData.sub_units);

    const doHideSubUnits   = hasHiddenSubUnit(period, restrictPeriod, restrictSubUnits, hideSubUnits);
    const showNoAvailabilityModal = () => showModal(
        "Sorry!",
        "We couldn\'t find an available arrival date for this unit within the next 14 months.<br><br>Please try another unit or contact us."
    );
    
    // If there aren't any other options for categories and units, hide the dropdowns
    if (clientData.units.length == 1 && clientData.categories.length == 1) {
        toggle(elById('atb-unit-row'), false);
        toggle(elById('atb-category-row'), false);
    }

    // if using sub-units, unit dropdown is visible so filter it by the correct category id
    if (subUnitId > 0 && unitId !== subUnitId && bookingData.av_sub_units.length > 0) {
        const subUnitData = clientData.sub_units.find(sub_unit => sub_unit.id == subUnitId);

        // The preset response already applies pet permissions for these dates,
        // including seasonal pitches whose ordinary pet flag may be off.
        const subUnitsToShow = bookingData.av_sub_units;

        toggle(elById('atb-sub-unit-row'), !doHideSubUnits, 'flex');
        showDropdownOptsByParentId('unit', categoryId);
        showDropdownOptsInArray('sub-unit', subUnitsToShow);

        summary.updateSummarySubUnitUI(subUnitData.name, !doHideSubUnits);
    } else if ((!bookingData.is_single_unit || subUnitId > 0) && bookingData.av_sub_units.length === 0 && !missingArrivalDate) {
        // No sub-units available — show the message regardless of hide_sub_units mode so the
        // customer knows they cannot proceed. Guard on !missingArrivalDate so we don't show
        // "NO UNITS AVAILABLE" prematurely while the calendar is still searching for a date.
        const noUnitsMessage = petsFilterActive ? 'NO PET FRIENDLY UNITS AVAILABLE' : 'NO UNITS AVAILABLE';
        setDropdownMessage(elById('atb-sub-unit'), noUnitsMessage);
        toggle(elById('atb-sub-unit-row'), true, 'flex');
        resetExtrasUI();
    }

    // Build the correct party member details containers based upon what's already set by smarty in the dom
    buildPartyMemberDetailsContainers();

    // pluralise/depluralise quantity labels in the booking summary as PHP doesn't currently do this for us
    formatSummaryLabels();

    toggle(elById('atb-sub-unit-pitch-type-row'), usingPitchFilter);
    if (usingPitchFilter) {
        const pitchTypeInputEl = elById('atb-sub-unit-pitch-type');
        showDropdownOptsInArray('sub-unit-pitch-type', bookingData.av_pitch_types);
        resetDropdown(pitchTypeInputEl, false);
    }

    toggle(elById('atb-filter-pet-friendly-row'), usingPetsFilter, 'flex');
    const petsCheckbox = elById('atb-filter-pet-friendly');
    if (petsCheckbox) petsCheckbox.checked = Boolean(petsFilterActive);
    
    // A missing-date preset starts with an intentionally unavailable placeholder date while
    // the calendar finds the next available stay. Only show this error for an explicit date.
    if (!isBookingAvailable && !missingArrivalDate) {
        console.warn('Booking is not available and the date range is invalid');
        toggle(elById('atb-sub-unit-row'), false);
        showModal(
            "Oops!", 
            "It appears the dates you\'ve selected have already been booked.<br><br>Please try again with different dates.",
            {
                buttonCallback: () => {                    
                    elById('atb-calendar-btn')?.click();
                    toggle(elById('atb-party-size-row'), false);
                    toggle(elById('atb-party-members-section'), false);
                },
                buttonOpts: { once: true }
            }
        )
    }

    // Calendar button — re-renders the calendar with the current selection every time
    // the drawer is opened, so going back to the calendar keeps the existing date range.
    const calendarBtnEl = elById('atb-calendar-btn');
    if (calendarBtnEl) {
        // Remove previous listeners by cloning and replacing button
        const cleanBtn = calendarBtnEl.cloneNode(true);
        calendarBtnEl.replaceWith(cleanBtn);

        cleanBtn.addEventListener(
            'click',
            () => {
                // Preset links without a date should find and select the next available date.
                const shouldFindFirstAvailable = Boolean(missingArrivalDate);
                // Read latest values from DOM/state, not stale closure variables
                const currentArrivalYmd = $('#atb-config').attr('data-arrival_date_full') || startDate;
                const currentPeriod = parseIntb10(elById('atb-period').value) || period;
                calendar.generate(
                    shouldFindFirstAvailable ? '' : currentArrivalYmd,
                    shouldFindFirstAvailable ? 0 : currentPeriod,
                    {
                        usingPreset: !shouldFindFirstAvailable && isBookingAvailable,
                        findFirstAvailable: shouldFindFirstAvailable,
                        autoSelectFirstAvailable: shouldFindFirstAvailable,
                        onFirstAvailableNotFound: shouldFindFirstAvailable ? showNoAvailabilityModal : null
                    }
                );
            }
        );
    }

    if (missingArrivalDate) {
        // Kick off the lookup immediately so the backend sets arrival/period before the user continues.
        calendar.generate('', 0, {
            usingPreset: false,
            findFirstAvailable: true,
            autoSelectFirstAvailable: true,
            onFirstAvailableNotFound: showNoAvailabilityModal
        });
    }

    if (usingPartySize) {
        togglePartySizeDropdown(true);
        setDropdown(elById('atb-party-size'), String(partySize));        
    } else {
        const partyMemberSteppers = filterElementsByParentId(qsAll('.atb-party.atb-stepper'), unitId);
        initialisePartyMemberSteppers(partyMemberSteppers, partyMembers, false);
    }

    // Enable continue buttons
    qsAll('.atb-next-stage-btn')
        .forEach(button => (button.disabled = false));

    // Hide loading overlay
    toggle(elById('atb-loading-overlay-spinner'), false);

    // Show the occupancy party-size dropdown
    toggle(elById('atb-party-size2-row'), availabilityType == AvailabilityType.Occupancy, 'flex');

    // Filter party-size dropdown
    filterDropdownByOccupancy('party-size', unitMaxOccupancy);

    const availableSubUnits = missingArrivalDate ? null : bookingData.av_sub_units;
    updatePitchMapButton(unit, availableSubUnits);
    
    summary.updateSummary(bookingData.costs);
}

function updateOnPaymentChange(bookingData) {
    console.log('update on payment change ran');
    const chosenPaymentType = bookingData.chosen_payment_type;
    updatePaymentSelectedClass(chosenPaymentType);
    summary.updateSummary(bookingData.costs);
}

/**
 * Unticks the "bring a dog"/pet-friendly filter checkbox so the customer can re-select it
 * after changing a previous criterion (category, unit or arrival date)
 */
function resetPetFriendlyFilterCheckbox() {
    const petFriendlyCheckbox = elById('atb-filter-pet-friendly');
    if (petFriendlyCheckbox) petFriendlyCheckbox.checked = false;
}

function hasHiddenSubUnit(lengthOfStay, restrictSubUnitPeriod, restrictSubUnits, hideSubUnits) {
    return (lengthOfStay < restrictSubUnitPeriod && restrictSubUnits) ||
        Boolean(hideSubUnits);
}

function updatePitchMapButton(unit = null, availableSubUnits = null) {
    const mapAction = elById('atb-pitch-map-action');
    if (!mapAction) return;

    const subUnitRow = elById('atb-sub-unit-row');
    const hasVisibleSubUnitDropdown =
        subUnitRow &&
        !subUnitRow.classList.contains('d-none') &&
        subUnitRow.style.display !== 'none';

    const doShow = hasVisibleSubUnitDropdown && shouldShowPitchMap(unit, availableSubUnits);
    mapAction.classList.toggle('d-none', !doShow);
}

export { 
    updateOnSiteChange,
    updateOnCategoryChange,
    updateOnUnitChange,
    updateOnOccupancyPartySizeChange,
    updateOnPeriodChange,    
    updateOnSubUnitChange,
    updateOnPaymentChange,
    initPresetUI,
    updatePitchMapButton,
    showModal
};
